import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import { Power1, Power2 } from 'gsap/gsap-core';

gsap.registerPlugin(ScrollToPlugin);

/**
 * Initial preloader loading animation.
 */
const initPreloaderAnimation = () => {
    const tl = gsap.timeline();

    if (document.body.classList.contains('neos-backend')) return;

    tl.to('#light-preloader', { duration: 2, autoAlpha: 0, ease: Power2.easeOut }, 0);

    const blur = document.querySelector('#hero-blur');

    // This is a hack to work around the broken Safari webkit renderer, which fails to properly render a blurred
    // backdrop when the associated element is not visible on page load
    const safariBlurFix = () => {
        if (!blur) return;

        // Get the intended backdrop property. Safari is the only browser which does not yet have proper
        // backdrop support and uses the -webkit- prefix, so any other browser will skip the timeout entirely
        const originalWebkitBackdropFilter = window.getComputedStyle(blur)['-webkit-backdrop-filter'];

        if (!originalWebkitBackdropFilter) return;

        setTimeout(() => {
            // We're removing the backdrop filter. It's important that the rule still defines a blur (so we
            // can't use '' or null here), otherwise Safari will not detect a change in the blur state
            blur.style['-webkit-backdrop-filter'] = 'blur(0px)';

            // In the next animation frame, we restore the original backdrop filter. This will cause the
            // renderer to re-interpret the style and enable the blur.
            window.requestAnimationFrame(() => (blur.style['-webkit-backdrop-filter'] = originalWebkitBackdropFilter));
        }, 1000);
    };

    if (sessionStorage.getItem('preloader-loaded') !== 'true') {
        tl.to('#preloader', { delay: 0.5, duration: 2, top: '100%', ease: Power2.easeOut }, 0);
        tl.to('#preloader-logo', { delay: 0.5, duration: 1.5, y: '2rem', ease: Power2.easeOut }, 0);

        sessionStorage.setItem('preloader-loaded', 'true');

        if (!document.querySelector('#hero')) return;

        gsap.set('#header', { y: '-100%' });
        gsap.set('#hero .read-more', { y: '100%' });
        if (blur) gsap.set(blur, { x: '-100%', autoAlpha: 0 });
        gsap.set('#hero-content > *', { x: '-7rem', autoAlpha: 0 });

        tl.to('#header, #hero .read-more', { delay: 0.5, duration: 1.5, y: 0, ease: Power2.easeOut }, 1);
        if (blur) tl.to(blur, { delay: 0.5, duration: 2.5, x: 0, autoAlpha: 1, ease: Power2.easeOut }, 2);
        tl.to('#hero-content > *', { delay: 2, duration: 2.5, x: 0, autoAlpha: 1, ease: Power2.easeOut }, 2);

        // Remove empty transform style to fix mobile navigation height.
        setTimeout(() => document.querySelector('#header')?.style?.setProperty('transform', ''), 5500);
        safariBlurFix();
    } else {
        if (!document.querySelector('#hero')) return;

        if (blur) gsap.set(blur, { autoAlpha: 0 });
        gsap.set('#hero-content > *', { x: '-3rem', autoAlpha: 0 });

        if (blur) tl.to(blur, { delay: 1, duration: 3, autoAlpha: 1, ease: Power2.easeOut }, 0);
        tl.to('#hero-content > *', { delay: 2, duration: 1.5, x: 0, autoAlpha: 1, ease: Power1.easeOut }, 0);

        safariBlurFix();
    }
};

const initHeroScrollAnimation = () => {
    const heroBtn = document.querySelector('#hero .read-more');
    let main = document.querySelector('#main-content');

    if (!main)
        main = document.querySelector('.nodetype-element--jvmtech-site-content-hero')
            ?.closest('.neos-contentcollection')
            ?.nextSibling;

    if (!heroBtn || !main) return;

    heroBtn.addEventListener('click', () =>
        gsap.to(window, {
            duration: 3,
            scrollTo: { y: main, offsetY: 0 },
            ease: Power2.easeInOut,
        })
    );
};

const initBackToTopScrollAnimation = () => {
    const backToTop = document.querySelector('footer .back-to-top');
    const header = document.querySelector('#header');

    if (!backToTop || !header) return;

    backToTop.addEventListener('click', () =>
        gsap.to(window, {
            duration: 5,
            scrollTo: { y: header, offsetY: 56 },
            ease: Power2.easeInOut,
        })
    );
};

window.addEventListener('load', () => {
    initPreloaderAnimation();
    initHeroScrollAnimation();
    initBackToTopScrollAnimation();
});
